import { useEffect, useRef, useCallback, useState } from 'react';
import timerService from '../services/timerService';

function urlBase64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== 'string') {
    throw new Error('VAPID public key is empty or invalid.');
  }
  // Strip whitespace, newlines, and any accidental surrounding quotes from env vars
  const cleanString = base64String.trim().replace(/^["']+|["']+$/g, '');
  const padding = '='.repeat((4 - (cleanString.length % 4)) % 4);
  const base64 = (cleanString + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  if (outputArray.length !== 65 || outputArray[0] !== 4) {
    console.warn(`VAPID public key format warning: expected 65 bytes starting with 0x04 (uncompressed P-256 point), but got ${outputArray.length} bytes starting with 0x${outputArray[0]?.toString(16)}. Key string was: "${cleanString}"`);
  }
  return outputArray;
}

/**
 * Manages service worker registration and provides a messaging interface
 * for scheduling/cancelling timer notifications and Web Push subscriptions.
 */
const useServiceWorker = () => {
  const swRef = useRef(null); // ServiceWorkerRegistration
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);

  // ── Register SW on mount ──────────────────────────────────────────
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.log('Service workers not supported in this browser.');
      return;
    }

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        swRef.current = registration;
        console.log('Service Worker registered:', registration.scope);
        if ('pushManager' in registration) {
          registration.pushManager.getSubscription().then((sub) => {
            setIsPushSubscribed(!!sub);
          });
        }
      })
      .catch((err) => {
        console.error('Service Worker registration failed:', err);
      });

    // Listen for SW messages
    const handleMessage = (event) => {
      console.log('Message from SW:', event.data);
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  // ── Send a message to the active SW ──────────────────────────────
  const sendToSW = useCallback((message) => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          const target = registration.active || navigator.serviceWorker.controller;
          if (target) {
            target.postMessage(message);
          } else {
            console.warn('No active service worker to send message to.');
          }
        })
        .catch((err) => console.error('Failed to get SW ready:', err));
    }
  }, []);

  const scheduleTimerNotification = useCallback(
    (endTimeMs, mode) => {
      sendToSW({ type: 'SCHEDULE_NOTIFICATION', endTimeMs, mode });
    },
    [sendToSW]
  );

  const cancelTimerNotification = useCallback(() => {
    sendToSW({ type: 'CANCEL_NOTIFICATION' });
  }, [sendToSW]);

  const sendTestNotification = useCallback(() => {
    sendToSW({ type: 'SKIP_TO_NOTIF', mode: 'TEST' });
  }, [sendToSW]);

  const showNotificationViaSW = useCallback(async (title, body, mode = 'FOCUS') => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if ('serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.showNotification(title, {
          body,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'pomodoro-timer',
          renotify: true,
          requireInteraction: false,
          vibrate: [200, 100, 200],
          data: { mode, url: '/timer' },
        });
        return;
      } catch (err) {
        console.warn('SW showNotification failed, trying fallback:', err);
      }
    }
    try {
      new Notification(title, { body, icon: '/logo192.png' });
    } catch (err) {
      console.warn('Fallback new Notification failed:', err);
    }
  }, []);

  const subscribeToWebPush = useCallback(async () => {
    if (!window.isSecureContext && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      alert('🔒 Web Push Alerts require a secure HTTPS connection! You are accessing this site via insecure HTTP. Please access the site using https://');
      return false;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('⚠️ Web Push is not supported on this browser or device. On iPhone, make sure the app is Added to Home Screen.');
      return false;
    }
    try {
      // Re-register or ensure registration exists explicitly so we don't hang on .ready
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      // Wait for active worker with a 10-second timeout so it never hangs silently
      const activeReg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Service Worker activation timed out after 10 seconds. Check console or SSL.')), 10000)),
      ]);
      const vapidKey = await timerService.getVapidKey();
      if (!vapidKey) {
        alert('❌ Could not retrieve VAPID public key from backend server.');
        console.error('No VAPID key received from backend.');
        return false;
      }
      const convertedKey = urlBase64ToUint8Array(vapidKey);
      const sub = await activeReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey,
      });
      await timerService.subscribePush(sub.toJSON());
      setIsPushSubscribed(true);
      return true;
    } catch (err) {
      console.error('Failed to subscribe to Web Push:', err);
      alert(`❌ Web Push Enable Failed: ${err.message || err}`);
      return false;
    }
  }, []);

  const unsubscribeFromWebPush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await timerService.unsubscribePush(sub.endpoint);
        await sub.unsubscribe();
      }
      setIsPushSubscribed(false);
    } catch (err) {
      console.error('Failed to unsubscribe from Web Push:', err);
      alert(`❌ Unsubscribe Failed: ${err.message || err}`);
    }
  }, []);

  const sendTestPushAlert = useCallback(async () => {
    try {
      await timerService.testPush();
    } catch (err) {
      console.error('Test push failed:', err);
      alert(`❌ Test Push Failed: ${err.response?.data?.message || err.message || err}`);
    }
  }, []);

  return {
    scheduleTimerNotification,
    cancelTimerNotification,
    showNotificationViaSW,
    sendTestNotification,
    isPushSubscribed,
    subscribeToWebPush,
    unsubscribeFromWebPush,
    sendTestPushAlert,
  };
};

export default useServiceWorker;
